/* ── Which routes render without a login ───────────────────────────────
   ONE list, read by both auth gates.

   It lived as two copies — one in RenderOnAnonymous, one in
   RenderOnAuthenticated — and both have to agree or the page breaks in a way
   that is invisible until someone opens the link on a real device:

     - listed in neither  -> the visitor gets the marketing homepage
     - listed in only one -> a blank screen, or the homepage inside an iframe

   That has now cost us the same bug twice. The family pass (2026-09-07) was
   dead on arrival because only `/visit` was listed, and the website embed
   (2026-09-10) rendered the OpenSky homepage inside the customer's own page
   because `/e/` was in neither. Both were minted, shared, and only found by
   loading them for real.

   Every entry is deliberately narrow — one path prefix plus a single opaque
   segment — so no authenticated route can be reached by dressing up a URL. */

/** The public doors, each keyed on its own opaque token. */
const PUBLIC_PATTERNS: RegExp[] = [
  // Guided tour — the buyer and their family have no account.
  //   /visit/<join_code>  the lead's own link
  //   /pass/<token>       the family pass, forwarded by a lead
  //   /tour/<room_id>     the room itself, after admission or a reload
  /^\/(visit|pass|tour)\/[^/]+/,
  // Short share link — /s/<code> resolves the public project viewer.
  /^\/s\/[^/]+/,
  // Website embed — /e/<key>, inside an iframe on the developer's OWN site.
  // The visitor is anonymous by definition and must never see our app.
  /^\/e\/[^/]+/,
];

/**
 * Is this path public — i.e. should it render with no Keycloak session?
 *
 * `search` matters for exactly one case: the project-detail route is public
 * ONLY when it carries a share code (`?ref=`). Gating on the pair is what stops
 * any authenticated project page being opened anonymously by appending `?ref=`.
 */
export const isPublicPath = (pathname: string, search = ""): boolean => {
  const path = String(pathname || "");
  if (PUBLIC_PATTERNS.some((re) => re.test(path))) return true;
  try {
    const hasRef = !!new URLSearchParams(search).get("ref");
    const isProjectDetail = /^\/c\/[^/]+\/p\/[^/]+/.test(path);
    return hasRef && isProjectDetail;
  } catch {
    return false; // unparseable query — fall through to the normal auth check
  }
};

export default isPublicPath;
